/* eslint-disable react/display-name */
/* eslint-disable no-unused-vars */
import React, { useState, useRef, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Panel, PanelGroup } from 'react-resizable-panels';
import { isArray } from 'lodash';

import './style.scss';
import { ResizeHandle } from './ResizeHandle';
import { SideBar } from './SideBar';
import IconCollapse from 'svgIcons/MotionPortalIcons/IconCollapse';
import IconExpand from 'svgIcons/MotionPortalIcons/IconExpand';

const ThreeColumnResizableLayout = React.memo(({
    leftPanelDefaulSize = 25,
    leftPanelMinSize = 10,
    leftPanelCollapsedSize = 3,
    leftPanelCollapsedIcon = <IconExpand width={16} height={16} />,
    leftPanelExpandedIcon = <IconCollapse width={16} height={16} />,
    rightPanelCollapsedIcon = <IconCollapse width={16} height={16} />,
    rightPanelExpandedIcon = <IconExpand width={16} height={16} />,
    centerPanelMinSize = 10,
    centerPanelDefaultSize = 50,
    centerPanelCollapsedSize = 3,
    centerPanelCollapsedIcon = <IconExpand width={16} height={16} />,
    centerPanelExpandedIcon = <IconCollapse width={16} height={16} />,
    leftPanelHeader = '',
    rightPanelHeader = '',
    rightPanelMinSize = 10,
    rightPanelDefaultSize = 25,
    rightPanelCollapsedSize = 3,
    onLeftPanelCollapse,
    onRightPanelCollapse,
    children, // [leftContent, centerContent, rightContent]
    onResizeLeftPanel = () => { },
    onResizeCenterPanel = () => { },
    onResizeRightPanel = () => { },
    mainLayoutclass,
    isForHierarchyView,
    isForConditionOverview,
    isCenterCollapsible = false,
    isLeftCollapsible = true,
    conditionIndicesView,
    setConditionIndicesView,
    requestHierarchyData,
    lastUpdatedTimestamp,
    assetsLastUpdatedTimestamp,
    activeTab
}) => {
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [centerSidebarOpen, setCenterSidebarOpen] = useState(true);
    const [rightSidebarOpen, setRightSidebarOpen] = useState(true);
    const leftPanelRef = useRef(null);
    const centerPanelRef = useRef(null);
    const rightPanelRef = useRef(null);
    // Memoize children to avoid unnecessary renders
    const leftContent = useMemo(() => children && isArray(children) ? children[0] : children, [children]);
    const centerContent = useMemo(() => children && children?.length > 1 && children[1], [children]);
    const rightContent = useMemo(() => children && children?.length > 2 && children[2], [children]);
    const onLeftPanelClick = useCallback(() => {
        if (leftPanelRef?.current) {
            if (sidebarOpen) {
                leftPanelRef?.current?.collapse();
                setSidebarOpen(false);
                onLeftPanelCollapse && onLeftPanelCollapse(true);
            } else {
                leftPanelRef?.current?.expand();
                setSidebarOpen(true);
                onLeftPanelCollapse && onLeftPanelCollapse(false);
            }
        }
    }, [sidebarOpen, onLeftPanelCollapse]);
    const onRightPanelClick = useCallback(() => {
        if (rightPanelRef?.current) {
            if (rightSidebarOpen) {
                rightPanelRef?.current?.collapse();
                setRightSidebarOpen(false);
                onRightPanelCollapse && onRightPanelCollapse(true);
            } else {
                rightPanelRef?.current?.expand();
                setRightSidebarOpen(true);
                onRightPanelCollapse && onRightPanelCollapse(false);
            }
        }
    }, [rightSidebarOpen, onRightPanelCollapse]);

    const onCenterPanelClick = useCallback(() => {
        if (!isCenterCollapsible || !centerPanelRef?.current) {
            return;
        }
        if (centerSidebarOpen) {
            centerPanelRef.current.collapse();
            setCenterSidebarOpen(false);
        } else {
            centerPanelRef.current.expand();
            setCenterSidebarOpen(true);
        }
    }, [isCenterCollapsible, centerSidebarOpen]);

    return (
        <PanelGroup direction='horizontal' className={`three-column-panel-group ${mainLayoutclass || ''}`}>
            <Panel
                ref={leftPanelRef}
                id='left-panel'
                defaultSize={leftPanelDefaulSize}
                collapsible={isLeftCollapsible}
                minSize={leftPanelMinSize}
                collapsedSize={leftPanelCollapsedSize}
                onCollapse={() => setSidebarOpen(false)}
                onExpand={() => setSidebarOpen(true)}
                onResize={onResizeLeftPanel}>
                <SideBar
                    title={leftPanelHeader}
                    expanded={sidebarOpen}
                    onToggle={onLeftPanelClick}
                    expandedIcon={leftPanelExpandedIcon}
                    collapsedIcon={leftPanelCollapsedIcon}
                    showCollapseButton={!isForHierarchyView && centerContent}
                    isForConditionOverview={isForConditionOverview}
                    conditionIndicesView={conditionIndicesView}
                    setConditionIndicesView={setConditionIndicesView}
                    requestHierarchyData={requestHierarchyData}
                    lastUpdatedTimestamp={lastUpdatedTimestamp}
                    assetsLastUpdatedTimestamp={assetsLastUpdatedTimestamp}
                    activeTab={activeTab}
                >
                    {leftContent}
                </SideBar>
            </Panel>
            {centerContent && <><ResizeHandle className={`ResizeHandle2 ${!sidebarOpen ? 'd-none' : ''}`} />
                <Panel
                    ref={centerPanelRef}
                    id='center-panel'
                    minSize={centerPanelMinSize}
                    defaultSize={centerPanelDefaultSize}
                    collapsible={isCenterCollapsible}
                    collapsedSize={centerPanelCollapsedSize}
                    onCollapse={() => setCenterSidebarOpen(false)}
                    onExpand={() => setCenterSidebarOpen(true)}
                    onResize={onResizeCenterPanel}
                >
                    <div className='center-panel-wrapper'>
                        {centerContent}
                        {isCenterCollapsible &&
                            <button
                                type='button'
                                aria-label={centerSidebarOpen ? 'Collapse center panel' : 'Expand center panel'}
                                onClick={onCenterPanelClick}
                                className={`column-sidebar-toggle-button ${centerSidebarOpen ? 'expanded' : 'collapsed'} center`}
                            >
                                {centerSidebarOpen ? centerPanelExpandedIcon : centerPanelCollapsedIcon}
                            </button>
                        }
                    </div>
                </Panel></>}
            {rightContent && <><ResizeHandle className={`ResizeHandle2 ${!rightSidebarOpen ? 'd-none' : ''}`} />
                <Panel
                    defaultSize={rightPanelDefaultSize}
                    collapsible={true}
                    minSize={rightPanelMinSize}
                    collapsedSize={rightPanelCollapsedSize}
                    id='right-panel'
                    onCollapse={() => setRightSidebarOpen(false)}
                    onExpand={() => setRightSidebarOpen(true)}
                    ref={rightPanelRef}
                    onResize={onResizeRightPanel}>
                    <SideBar
                        title={rightPanelHeader}
                        isRight={true}
                        expanded={rightSidebarOpen}
                        onToggle={onRightPanelClick}
                        expandedIcon={rightPanelExpandedIcon}
                        collapsedIcon={rightPanelCollapsedIcon}
                        showCollapseButton={!isForHierarchyView && rightContent}
                    >
                        {rightContent}
                    </SideBar>
                </Panel></>}
        </PanelGroup>
    );
});

ThreeColumnResizableLayout.propTypes = {
    leftPanelDefaulSize: PropTypes.number,
    leftPanelCollapsedSize: PropTypes.number,
    leftPanelMinSize: PropTypes.number,
    centerPanelMinSize: PropTypes.number,
    centerPanelDefaultSize: PropTypes.number,
    centerPanelCollapsedSize: PropTypes.number,
    centerPanelCollapsedIcon: PropTypes.node,
    centerPanelExpandedIcon: PropTypes.node,
    leftPanelCollapsedIcon: PropTypes.node,
    leftPanelExpandedIcon: PropTypes.node,
    rightPanelCollapsedIcon: PropTypes.node,
    rightPanelExpandedIcon: PropTypes.node,
    leftPanelHeader: PropTypes.string,
    rightPanelHeader: PropTypes.string,
    rightPanelMinSize: PropTypes.number,
    rightPanelDefaultSize: PropTypes.number,
    rightPanelCollapsedSize: PropTypes.number,
    onLeftPanelCollapse: PropTypes.func,
    onRightPanelCollapse: PropTypes.func,
    children: PropTypes.arrayOf(PropTypes.node), // [centerContent, rightContent]
    onResizeLeftPanel: PropTypes.func,
    onResizeCenterPanel: PropTypes.func,
    onResizeRightPanel: PropTypes.func,
    mainLayoutclass: PropTypes.string,
    isForHierarchyView: PropTypes.bool,
    isForConditionOverview: PropTypes.bool,
    isCenterCollapsible: PropTypes.bool,
    isLeftCollapsible: PropTypes.bool,
    conditionIndicesView: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setConditionIndicesView: PropTypes.func,
    requestHierarchyData: PropTypes.func,
    lastUpdatedTimestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
    assetsLastUpdatedTimestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
    activeTab: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ThreeColumnResizableLayout;
